import { useEffect, useState } from "react";
import { IconUser } from "@tabler/icons-react";
import type { UserInfo } from "../api/queries/userQueries";

interface AccountProfileSubTabProps {
  userInfo: UserInfo | undefined;
  isSaving: boolean;
  onSave: (username: string) => void;
}

export function AccountProfileSubTab({
  userInfo,
  isSaving,
  onSave,
}: AccountProfileSubTabProps) {
  const [usernameDraft, setUsernameDraft] = useState("");

  useEffect(() => {
    if (userInfo?.username) {
      setUsernameDraft(userInfo.username);
    }
  }, [userInfo?.username]);

  const trimmedDraft = usernameDraft.trim();
  const unchanged = trimmedDraft === (userInfo?.username ?? "").trim();
  const canSave = trimmedDraft.length > 0 && !unchanged && !isSaving;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-linear-to-br from-emerald-500 to-teal-500 text-white shadow-md">
          <IconUser size={24} stroke={2} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Account</h2>
          <p className="text-sm text-gray-600">
            Your display name appears in outgoing emails and across the app
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <label
          htmlFor="username"
          className="block text-sm font-semibold text-gray-700 mb-2"
        >
          Display name
        </label>
        <input
          id="username"
          type="text"
          value={usernameDraft}
          onChange={(e) => setUsernameDraft(e.target.value)}
          maxLength={200}
          placeholder="Your name"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
        />
        {userInfo?.email && (
          <p className="mt-2 text-sm text-gray-500">
            Signed in as{" "}
            <span className="font-medium text-gray-700">{userInfo.email}</span>
          </p>
        )}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => onSave(trimmedDraft)}
            disabled={!canSave}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
          >
            {isSaving ? "Saving..." : "Save name"}
          </button>
        </div>
      </div>
    </div>
  );
}
